var _excluded = ["node", "buildKey", "useMeta", "useSignal"];
function _objectWithoutPropertiesLoose(source, excluded) { if (source == null) return {}; var target = {}; var sourceKeys = Object.keys(source); var key, i; for (i = 0; i < sourceKeys.length; i++) { key = sourceKeys[i]; if (excluded.indexOf(key) >= 0) continue; target[key] = source[key]; } return target; }
/* eslint-disable react/prop-types */
import React from 'react';
import { DndContext, useDraggable, useDroppable, KeyboardSensor, PointerSensor, useSensor, useSensors } from '@dnd-kit/core';
import useCallDetectorInhibitor from "../../hooks/useCallDetectorInhibitor";

// eslint-disable-next-line func-names
import { jsx as _jsx } from "react/jsx-runtime";
var DragAndDrop = function DragAndDrop(Component) {
  return function (_ref) {
    var node = _ref.node,
      buildKey = _ref.buildKey,
      useMeta = _ref.useMeta,
      useSignal = _ref.useSignal,
      rest = _objectWithoutPropertiesLoose(_ref, _excluded);
    var key = buildKey(node);
    var _useDroppable = useDroppable({
        id: "droppable-" + key,
        data: node
      }),
      setNodeRefDroppable = _useDroppable.setNodeRef;
    var _useDraggable = useDraggable({
        id: "draggable-" + key,
        data: node
      }),
      setNodeRefDraggable = _useDraggable.setNodeRef,
      attributes = _useDraggable.attributes,
      _useDraggable$listene = _useDraggable.listeners,
      onPointerDown = _useDraggable$listene.onPointerDown,
      onKeyDown = _useDraggable$listene.onKeyDown;
    var _useMeta = useMeta('isDragHover', false),
      isDragHover = _useMeta[0],
      setIsDragHover = _useMeta[1];
    useSignal('dragHover', setIsDragHover);
    var _useMeta2 = useMeta('isDragged', false),
      isDragged = _useMeta2[0],
      setIsDragged = _useMeta2[1];
    var onDragStartHandler = React.useCallback(function () {
      setIsDragged(true);
    }, [setIsDragged]);
    useSignal('dragStart', onDragStartHandler);
    var onDragEndHandler = React.useCallback(function () {
      setIsDragged(false);
    }, [setIsDragged]);
    useSignal('dragEnd', onDragEndHandler);
    var _useCallDetectorInhib = useCallDetectorInhibitor(onPointerDown),
      getOnPointerDown = _useCallDetectorInhib[0],
      controlledPointerDown = _useCallDetectorInhib[1];
    var props = Object.assign({}, rest, {
      getOnPointerDown: getOnPointerDown,
      node: node,
      useMeta: useMeta,
      useSignal: useSignal,
      isDraggable: true,
      isDragged: isDragged,
      isDragHover: isDragHover
    });
    return (
      /*#__PURE__*/
      // eslint-disable-next-line jsx-a11y/no-static-element-interactions
      _jsx("div", Object.assign({
        className: "draggable",
        ref: setNodeRefDraggable,
        onKeyDown: onKeyDown,
        onPointerDown: controlledPointerDown
      }, attributes, {
        children: /*#__PURE__*/_jsx("div", {
          className: "droppable",
          ref: setNodeRefDroppable,
          children: /*#__PURE__*/_jsx(Component, Object.assign({}, props))
        })
      }))
    );
  };
};
DragAndDrop.handledProps = [];
var DragAndDropContext = function DragAndDropContext(_ref2) {
  var _ref2$compare = _ref2.compare,
    compare = _ref2$compare === void 0 ? function () {
      return 1;
    } : _ref2$compare,
    children = _ref2.children;
  var _React$useState = React.useState(null),
    setInitialState = _React$useState[1];
  var cancelDragHandler = React.useCallback(function () {
    setInitialState(function (initiaState) {
      if (initiaState) {
        var node = initiaState.node,
          parentNode = initiaState.parentNode,
          position = initiaState.position;
        node.sendSignal('dragEnd');
        node.parent.sendSignal('dragHover', false);
        parentNode.addChild(node, function () {
          return position;
        });
      }
      return null;
    });
  }, [setInitialState]);
  var dragHandler = React.useCallback(function (event, isDragEnd) {
    if (isDragEnd === void 0) {
      isDragEnd = false;
    }
    if (!(event != null && event.over)) {
      return false;
    }
    var childNode = event.active.data.current,
      parentNode = event.over.data.current;
    if (!parentNode || !childNode || childNode.isRoot()) {
      return false;
    }
    if (parentNode === childNode) {
      return true;
    }
    if (parentNode.children.includes(childNode)) {
      if (isDragEnd) {
        parentNode.sendSignal('dragHover', false);
      }
      return true;
    }
    // eslint-disable-next-line @typescript-eslint/no-shadow
    var hasNewChildInAncestry = parentNode.propagateToParent(function (node, hasNewChildInAncestry) {
      if (hasNewChildInAncestry) {
        return true;
      }
      return node === childNode;
    });
    if (hasNewChildInAncestry) {
      return false;
    }
    childNode.parent.sendSignal('dragHover', false);
    // On positionne le noeud en tant que premier enfant du parent
    // afin qu'il soit visible lors du DragAndDrop
    parentNode.addChild(childNode, function () {
      return 0;
    });
    if (!isDragEnd) {
      parentNode.sendSignal('dragHover', true);
    }
    parentNode.sendSignalToParent('expand');
    return true;
  }, []);
  var onDragEndHandler = React.useCallback(function (event) {
    if (dragHandler(event, true)) {
      setInitialState(function (initiaState) {
        if (initiaState) {
          var node = initiaState.node,
            parentNode = initiaState.parentNode,
            position = initiaState.position;
          var newParent = node.parent;
          // On positionne le noeud en utilisant la fonction de comparaison à la fin du DragAndDrop
          newParent.removeChild(node);
          newParent.addChild(node, function (brothers) {
            var index = brothers.findIndex(function (brother) {
              return compare(node, brother) <= 0;
            });
            if (index === -1) {
              return brothers.length;
            }
            return index;
          });
          node.sendSignal('dragEnd');
          if (node.parent !== parentNode) {
            node.sendSignalToParent('nodeDragged', {
              old: {
                parent: parentNode,
                position: position
              },
              "new": {
                parent: node.parent,
                position: node.parent.children.indexOf(node)
              }
            });
          }
        }
        return null;
      });
    } else {
      cancelDragHandler();
    }
  }, [dragHandler, cancelDragHandler, setInitialState]);
  var onDragStartHandler = React.useCallback(function (event) {
    var node = event.active.data.current;
    if (node.isRoot()) {
      return;
    }
    var parentNode = node.parent;
    var position = parentNode.children.indexOf(node);
    node.sendSignal('dragStart');
    setInitialState({
      node: node,
      parentNode: parentNode,
      position: position
    });
  }, [setInitialState]);
  var keyboardCodes = {
    start: [],
    cancel: ['Escape'],
    end: []
  };
  var pointerSensor = useSensor(PointerSensor);
  var keyboardSensor = useSensor(KeyboardSensor, {
    keyboardCodes: keyboardCodes
  });
  var sensors = useSensors(pointerSensor, keyboardSensor);
  return /*#__PURE__*/_jsx(DndContext, {
    sensors: sensors,
    onDragStart: onDragStartHandler,
    onDragOver: dragHandler,
    onDragEnd: onDragEndHandler,
    onDragCancel: cancelDragHandler,
    children: children
  });
};
DragAndDropContext.handledProps = [];
DragAndDrop.Context = DragAndDropContext;
export default DragAndDrop;